import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Dialog, Input, Select } from '../components/ui';
import { ConfirmDialog, EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import {
  checkFile,
  checkMediaUsage,
  deleteMedia,
  formatBytes,
  listMedia,
  mediaFileUrl,
  mediaThumbUrl,
  uploadMedia,
  type MediaRecord,
  type MediaUsage
} from '../api/media';

type Upload = { id: string; name: string; progress: number; status: 'uploading' | 'done' | 'error'; message?: string };

const TYPE_FILTERS = ['all', 'image', 'video'];

export function Media() {
  const { session } = useSession();
  const { notify } = useToast();
  const writable = (session?.role ?? 'viewer') !== 'viewer';
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [items, setItems] = useState<MediaRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<MediaRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MediaRecord | null>(null);
  const [usage, setUsage] = useState<MediaUsage | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await listMedia());
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load media.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleFiles(files: FileList | File[]) {
    const list = Array.from(files);
    for (const file of list) {
      const id = `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;
      const clientError = checkFile(file);
      if (clientError) {
        setUploads((current) => [...current, { id, name: file.name, progress: 0, status: 'error', message: clientError }]);
        continue;
      }
      setUploads((current) => [...current, { id, name: file.name, progress: 0, status: 'uploading' }]);
      try {
        await uploadMedia(file, (percent) =>
          setUploads((current) => current.map((item) => (item.id === id ? { ...item, progress: percent } : item)))
        );
        setUploads((current) => current.map((item) => (item.id === id ? { ...item, status: 'done', progress: 100 } : item)));
      } catch (cause) {
        setUploads((current) =>
          current.map((item) =>
            item.id === id ? { ...item, status: 'error', message: cause instanceof Error ? cause.message : 'Upload failed.' } : item
          )
        );
      }
    }
    await load();
  }

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesQuery = !needle || (item.name ?? '').toLowerCase().includes(needle) || (item.tags ?? []).join(' ').toLowerCase().includes(needle);
      const matchesType = typeFilter === 'all' || (item.mime ?? '').startsWith(typeFilter);
      return matchesQuery && matchesType;
    });
  }, [items, query, typeFilter]);

  async function requestDelete(record: MediaRecord) {
    setDeleteTarget(record);
    setUsage(null);
    try {
      setUsage(await checkMediaUsage(record.id));
    } catch {
      setUsage({ layouts: 0, deployments: 0 });
    }
  }

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / MEDIA</span>
          <h1>Media</h1>
        </div>
        {writable && (
          <Button type="button" onClick={() => inputRef.current?.click()}>
            Upload
          </Button>
        )}
      </header>

      {writable && (
        <div
          className={dragging ? 'drop-zone drop-zone-active' : 'drop-zone'}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            if (event.dataTransfer.files.length) void handleFiles(event.dataTransfer.files);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            className="visually-hidden"
            accept="image/png,image/jpeg,image/gif,image/webp,video/mp4"
            onChange={(event) => {
              if (event.target.files?.length) void handleFiles(event.target.files);
              event.target.value = '';
            }}
          />
          <p>Drag files here, or use Upload. PNG, JPEG, GIF, WEBP and MP4 up to 25 MB.</p>
        </div>
      )}

      {uploads.length > 0 && (
        <ul className="upload-list">
          {uploads.map((item) => (
            <li key={item.id}>
              <span>{item.name}</span>
              {item.status === 'uploading' && <span className="mono">{item.progress}%</span>}
              {item.status === 'done' && <Badge>done</Badge>}
              {item.status === 'error' && <Badge className="danger">{item.message}</Badge>}
            </li>
          ))}
          <li>
            <Button variant="quiet" type="button" onClick={() => setUploads([])}>
              Clear
            </Button>
          </li>
        </ul>
      )}

      <div className="toolbar">
        <Input aria-label="Search media" placeholder="Search name or tag" value={query} onChange={(event) => setQuery(event.target.value)} />
        <Select aria-label="Filter by type" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          {TYPE_FILTERS.map((option) => (
            <option key={option} value={option}>
              {option === 'all' ? 'All types' : option}
            </option>
          ))}
        </Select>
      </div>

      {loading && <LoadingState label="Loading media" />}
      {error && <ErrorState title="Media unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && visible.length === 0 && (
        <EmptyStateBlock title="No media" detail="Upload images or video to use in layouts." />
      )}

      {!loading && !error && visible.length > 0 && (
        <section className="media-grid">
          {visible.map((item) => (
            <figure className="media-card" key={item.id}>
              <button type="button" className="media-thumb" onClick={() => setPreview(item)} aria-label={`Preview ${item.name}`}>
                {item.mime?.startsWith('image/') ? (
                  <img src={mediaThumbUrl(item)} alt={item.name ?? 'media'} loading="lazy" />
                ) : (
                  <span className="media-video-mark">VIDEO</span>
                )}
              </button>
              <figcaption>
                <strong>{item.name}</strong>
                <span className="row-subtitle">
                  {item.mime} · {formatBytes(item.size)}
                  {item.width ? ` · ${item.width}×${item.height}` : ''}
                </span>
                {writable && (
                  <Button variant="danger" type="button" onClick={() => void requestDelete(item)}>
                    Delete
                  </Button>
                )}
              </figcaption>
            </figure>
          ))}
        </section>
      )}

      {preview && (
        <Dialog open title={preview.name ?? 'Preview'} onClose={() => setPreview(null)}>
          {preview.mime?.startsWith('image/') ? (
            <img className="media-preview" src={mediaFileUrl(preview)} alt={preview.name ?? 'media'} />
          ) : (
            <video className="media-preview" src={mediaFileUrl(preview)} controls />
          )}
          <dl className="kv">
            <dt>Type</dt>
            <dd>{preview.mime}</dd>
            <dt>Size</dt>
            <dd>{formatBytes(preview.size)}</dd>
            <dt>Dimensions</dt>
            <dd>{preview.width ? `${preview.width}×${preview.height}` : '—'}</dd>
          </dl>
        </Dialog>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete media"
        confirmWord="delete"
        detail={
          deleteTarget
            ? `${deleteTarget.name}${
                usage && (usage.layouts > 0 || usage.deployments > 0)
                  ? ` — used by ${usage.layouts} layout(s) and ${usage.deployments} deployment(s). They will break.`
                  : ''
              }`
            : ''
        }
        confirmLabel="Delete"
        onClose={() => {
          setDeleteTarget(null);
          setUsage(null);
        }}
        onConfirm={() => {
          const target = deleteTarget;
          setDeleteTarget(null);
          if (!target) return;
          void deleteMedia(target.id)
            .then(() => {
              notify('Media deleted');
              void load();
            })
            .catch((cause) => setError(cause instanceof Error ? cause.message : 'Delete failed.'));
        }}
      />
    </main>
  );
}
